// ============================================================================
// SCOPES DASHBOARD
// ============================================================================
// Dashboard showing CO2e breakdown by scope with stats, bar chart, and tables
// ============================================================================

import React, { useEffect, useState } from 'react';
import { useAuth } from '@contexts/AuthContext';
import { emissionsApi } from '../api/emissions.api';
import PageInfoButton from '@/components/PageInfoButton';

interface DashboardData {
  scope1: number;
  scope2: number;
  scope3: number;
  total: number;
  calculations: any[];
  scope3_categories?: { category: string; total_co2e: number }[];
}

const ScopesDashboard: React.FC = () => {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const companyId: number | undefined = (user as any)?.company_id;

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const params: { company_id?: number } = {};
        if (companyId) params.company_id = companyId;
        const raw = await emissionsApi.getDashboard(params);
        // Normalize: the dashboard API returns { emissions: { scope1_tco2e, ... } }
        // but this component reads { scope1, scope2, scope3, total }.
        const e = raw?.emissions ?? raw;
        setDashboard({
          scope1: Number(e?.scope1_tco2e ?? raw?.scope1 ?? 0),
          scope2: Number(e?.scope2_tco2e ?? raw?.scope2 ?? 0),
          scope3: Number(e?.scope3_tco2e ?? raw?.scope3 ?? 0),
          total: Number(e?.total_tco2e ?? raw?.total ?? 0),
          calculations: raw?.calculations ?? [],
          scope3_categories: raw?.scope3_categories ?? [],
        });
      } catch (err: any) {
        setError(err?.message ?? 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [companyId]);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-emerald-600 border-t-transparent" />
      </div>
    );
  }

  const total = dashboard?.total ?? 0;
  const s1 = dashboard?.scope1 ?? 0;
  const s2 = dashboard?.scope2 ?? 0;
  const s3 = dashboard?.scope3 ?? 0;

  const pct = (val: number) => (total > 0 ? Math.round((val / total) * 100) : 0);

  const fmt = (n: number) =>
    (n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

  return (
    <div className="p-6 space-y-6">
      <div>
        <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Scopes Dashboard</h1><PageInfoButton /></div>
        <p className="text-slate-500 mt-1">Carbon emissions breakdown by GHG Protocol scope</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total CO₂e', value: fmt(total), unit: 'tonne', color: 'text-slate-900' },
          { label: 'Scope 1', value: fmt(s1), unit: 'tonne', color: 'text-blue-700' },
          { label: 'Scope 2', value: fmt(s2), unit: 'tonne', color: 'text-violet-700' },
          { label: 'Scope 3', value: fmt(s3), unit: 'tonne', color: 'text-emerald-700' },
        ].map(card => (
          <div key={card.label} className="bg-white border rounded-xl p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{card.label}</p>
            <p className={`text-2xl font-bold mt-1 ${card.color}`}>{card.value}</p>
            <p className="text-xs text-slate-400 mt-0.5">{card.unit}</p>
          </div>
        ))}
      </div>

      {/* Scope Bar Breakdown */}
      <div className="bg-white border rounded-xl p-6">
        <h2 className="text-sm font-semibold text-slate-700 mb-4">Emissions Breakdown</h2>
        <div className="space-y-4">
          {[
            { label: 'Scope 1 – Direct Emissions', value: s1, pct: pct(s1), color: 'bg-blue-500' },
            { label: 'Scope 2 – Indirect Energy', value: s2, pct: pct(s2), color: 'bg-violet-500' },
            { label: 'Scope 3 – Value Chain', value: s3, pct: pct(s3), color: 'bg-emerald-500' },
          ].map(row => (
            <div key={row.label}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-700 font-medium">{row.label}</span>
                <span className="text-slate-500">{fmt(row.value)} t ({row.pct}%)</span>
              </div>
              <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full ${row.color} rounded-full transition-all duration-500`}
                  style={{ width: `${row.pct}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Calculations */}
      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-sm font-semibold text-slate-700">Recent Calculations</h2>
        </div>
        {!dashboard?.calculations?.length ? (
          <div className="px-6 py-8 text-center text-slate-400 text-sm">No calculations yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  <th className="px-4 py-3 text-left">Factor</th>
                  <th className="px-4 py-3 text-left">Scope</th>
                  <th className="px-4 py-3 text-right">Activity</th>
                  <th className="px-4 py-3 text-right">CO₂e (kg)</th>
                  <th className="px-4 py-3 text-left">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {dashboard.calculations.map((calc: any, i: number) => (
                  <tr key={calc.id ?? i} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 text-left text-slate-800">{calc.factor_name ?? calc.emission_factor_id ?? '—'}</td>
                    <td className="px-4 py-3 text-left">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                        {calc.scope ?? '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-left text-slate-600">
                      {calc.activity_amount} {calc.input_unit}
                    </td>
                    <td className="px-4 py-3 text-left font-semibold text-slate-800">
                      {fmt(calc.co2e_kg ?? calc.total_co2e_kg ?? 0)}
                    </td>
                    <td className="px-4 py-3 text-left text-slate-500">
                      {calc.created_at ? new Date(calc.created_at).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Scope 3 Category Breakdown */}
      {dashboard?.scope3_categories && dashboard.scope3_categories.length > 0 && (
        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h2 className="text-sm font-semibold text-slate-700">Scope 3 Category Breakdown</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  <th className="px-4 py-3 text-left">Category</th>
                  <th className="px-4 py-3 text-right">Total CO₂e (t)</th>
                  <th className="px-4 py-3 text-left">Share</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {dashboard.scope3_categories.map((row, i) => (
                  <tr key={i} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 text-left text-slate-800">{row.category}</td>
                    <td className="px-4 py-3 text-left font-semibold text-slate-800">{fmt(row.total_co2e)}</td>
                    <td className="px-4 py-3 text-left">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-emerald-400 rounded-full"
                            style={{ width: `${pct(row.total_co2e)}%` }}
                          />
                        </div>
                        <span className="text-xs text-slate-500 w-8 text-right">{pct(row.total_co2e)}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScopesDashboard;
